/**
 * SB2-W-01 — InfoIcon primitive.
 *
 * A `?` affordance that opens a tooltip with a `title` heading and
 * `content` body. Re-usable for any compliance / ISO-27001 / policy
 * rationale where we need to explain WHY a UI control is constrained,
 * without leaning on the toast subsystem.
 *
 * Christian's verbatim OQ-SB2-A2(b) instruction (2026-05-20):
 *   "Users should understand that this is not a technical issue —
 *    we try to follow ISO 27001 standards here."
 *
 * Where it lives: next to constrained controls (e.g. the role-edit
 * dropdown in apps/web/src/features/admin/users/UserDetailDrawer)
 * where the constraint isn't self-evident from the UI shape.
 *
 * Accessibility:
 *   - The IconButton uses `aria-label` so screen readers announce the
 *     purpose even when the tooltip isn't open.
 *   - The Tooltip is reachable via keyboard focus (MUI default).
 *   - `data-testid` is set so Playwright tests can target the icon
 *     deterministically across the page (multiple InfoIcons on a
 *     single screen need different testid values; pass `testId` to
 *     disambiguate).
 */
import {
  IconButton,
  Tooltip,
  Typography,
  type IconButtonProps,
} from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import type { ReactElement, ReactNode } from "react";
import { CircleHelpIcon } from "@aeriosiq/ui/icons";

export interface InfoIconProps
  extends Omit<IconButtonProps, "children" | "title" | "content"> {
  /**
   * Tooltip title — rendered as a `<Typography variant="subtitle2">`.
   * Keep short (≤80 chars); the rationale goes in `content`.
   */
  title: string;
  /**
   * Tooltip body — rendered as `<Typography variant="body2">`. May be
   * a string or any inline ReactNode.
   */
  content: ReactNode;
  /**
   * Optional `data-testid` for Playwright/Vitest. Defaults to
   * `info-icon`; pass a unique value when multiple icons live on the
   * same page.
   */
  testId?: string;
}

export function InfoIcon({
  title,
  content,
  testId = "info-icon",
  size = "small",
  sx,
  ...rest
}: InfoIconProps): ReactElement {
  const baseSx: SxProps<Theme> = { color: "text.secondary" };
  const callerSx: SxProps<Theme> =
    sx === undefined ? [] : Array.isArray(sx) ? sx : [sx];
  const mergedSx = ([baseSx] as SxProps<Theme>[]).concat(
    callerSx as SxProps<Theme>,
  );
  return (
    <Tooltip
      title={
        <>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            {title}
          </Typography>
          <Typography variant="body2">{content}</Typography>
        </>
      }
      // PointerOver+focus opens; Escape closes. Default MUI behaviour
      // — we just lengthen the leave delay slightly so users have
      // time to move their cursor onto the tooltip text to copy it.
      leaveDelay={150}
      placement="top"
      arrow
    >
      <IconButton
        size={size}
        aria-label={title}
        data-testid={testId}
        sx={mergedSx as SxProps<Theme>}
        {...rest}
      >
        <CircleHelpIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
}
